/**
 * Detects whether a five-year projection series is "event-driven" — a flat
 * plateau (pre-revenue $0, or a steady development burn) followed by a single
 * dominant one-time jump (a licensing upfront, acquisition, or milestone
 * payment). That shape is the honest financial reality for a drug / deep-tech /
 * IP-asset business, but on a line chart built for a ramping revenue build it
 * reads as a rendering glitch. The Business Case charts use this to switch into
 * an "event mode" that renders the jump as a deliberate milestone impulse.
 *
 * Detection runs on the REALISTIC series only and requires BOTH conditions,
 * which is what isolates "looks broken" from every legitimate ramp:
 *   (a) exactly ONE year-over-year step that is a dominant fraction of the range
 *   (b) every year before that step is a flat plateau
 * A steep exponential or hockey-stick ramp is NOT flagged, because its pre-jump
 * years are not flat.
 */

export interface ProjectionShape {
  isEventDriven: boolean;
  /** 0-based index of the event year (usually 4 = Year 5). -1 when not event-driven. */
  eventYearIndex: number;
  /** How the pre-event plateau reads: all-zero revenue, a negative burn, or a flat non-zero hold. */
  plateauKind: 'pre-revenue' | 'burn' | 'flat';
}

const NONE: ProjectionShape = {
  isEventDriven: false,
  eventYearIndex: -1,
  plateauKind: 'flat',
};

/** Fraction of the full range a single step must clear to count as the event. */
const DOMINANT_STEP = 0.6;
/** Max fraction of the range any pre-event step may move and still be "flat". */
const FLAT_STEP = 0.15;

export function analyzeProjectionShape(realistic: number[] | undefined): ProjectionShape {
  if (!Array.isArray(realistic) || realistic.length < 3) return NONE;

  const lo = Math.min(0, ...realistic);
  const hi = Math.max(...realistic);
  const range = hi - lo;
  if (range <= 0) return NONE;

  // Year-over-year change as a fraction of the full range.
  const rel = realistic.map((v, i) =>
    i === 0 ? 0 : Math.abs(v - realistic[i - 1]) / range,
  );

  // (a) The event is the FIRST dominant step — a big jump up out of the plateau.
  //     Steps AFTER it are a post-event tail (e.g. declining royalties after a
  //     licensing upfront) and do NOT disqualify detection; only the flatness of
  //     the years BEFORE the event matters.
  let e = -1;
  for (let i = 1; i < rel.length; i++) {
    if (rel[i] >= DOMINANT_STEP) {
      e = i;
      break;
    }
  }
  if (e < 1) return NONE;

  // (b) Every year before the event is a flat plateau.
  for (let i = 1; i < e; i++) {
    if (rel[i] >= FLAT_STEP) return NONE;
  }

  const eps = 0.005 * Math.max(hi, Math.abs(lo), 1);
  const plateau = realistic.slice(0, e);
  const allZero = plateau.every((v) => Math.abs(v) <= eps);
  const mostlyNeg = plateau.filter((v) => v < -eps).length > plateau.length / 2;

  return {
    isEventDriven: true,
    eventYearIndex: e,
    plateauKind: allZero ? 'pre-revenue' : mostlyNeg ? 'burn' : 'flat',
  };
}

/**
 * Classify a specific series' pre-event plateau against a SHARED event year
 * (so revenue and EBITDA annotate the same year, but each reads its own shape).
 */
export function plateauKindFor(
  series: number[],
  eventYearIndex: number,
): ProjectionShape['plateauKind'] {
  if (eventYearIndex < 1) return 'flat';
  const lo = Math.min(0, ...series);
  const hi = Math.max(...series);
  const eps = 0.005 * Math.max(hi, Math.abs(lo), 1);
  const plateau = series.slice(0, eventYearIndex);
  if (plateau.every((v) => Math.abs(v) <= eps)) return 'pre-revenue';
  if (plateau.filter((v) => v < -eps).length > plateau.length / 2) return 'burn';
  return 'flat';
}

/**
 * The deterministic, design-owned caption that guarantees an event-driven chart
 * reads as intentional. Single source of truth for the copy across the memo,
 * the command center, and the PDF. `fmt` formats a plain dollar number.
 */
export function buildEventCaption(
  kind: ProjectionShape['plateauKind'],
  eventYearIndex: number,
  pess: number,
  real: number,
  opt: number,
  fmt: (n: number) => string,
  realSeries?: number[],
): string {
  const EY = eventYearIndex + 1;
  const span = eventYearIndex === 1 ? 'Year 1' : `Years 1\u2013${eventYearIndex}`; // en dash
  const hasRange = !(pess === real && opt === real);
  const rangeClause = hasRange
    ? `, range ${fmt(Math.min(pess, opt))}\u2013${fmt(Math.max(pess, opt))}`
    : '';
  // A licensing deal pays an upfront, then smaller declining milestone / royalty
  // payments. Detect a meaningful post-event tail so the caption stops calling
  // it a "single payment" when the chart is actually drawing an upfront + tail.
  const eventMag = Math.abs(real);
  const hasTail =
    Array.isArray(realSeries) &&
    eventMag > 0 &&
    realSeries.slice(eventYearIndex + 1).some((v) => Math.abs(v) >= 0.08 * eventMag);
  if (kind === 'pre-revenue') {
    return hasTail
      ? `This is a pre-revenue, event-driven model: revenue holds at $0 through ${span}, then a deal lands in Year ${EY} (${fmt(real)}${rangeClause}), followed by smaller milestone payments in the years after. Flat years, one deal, then a declining tail is the expected shape here, not missing data.`
      : `This is a pre-revenue, event-driven model: revenue holds at $0 through ${span}, then a single payment lands in Year ${EY} (${fmt(real)}${rangeClause}). Flat years followed by one jump is the expected shape here, not missing data.`;
  }
  if (kind === 'burn') {
    return hasTail
      ? `EBITDA runs negative through the pre-revenue years on development burn, then turns positive in Year ${EY} when the deal lands, easing in the following years as the near-term milestone payments taper. The shape is real, and the chart is rendering it correctly.`
      : `EBITDA runs negative through the pre-revenue years on development burn, then jumps positive in Year ${EY} when that payment lands. That jump is real, and the chart is rendering it correctly.`;
  }
  return hasTail
    ? `This model holds flat through ${span}, then a deal lands in Year ${EY} (${fmt(real)}), with smaller milestone payments in the years after. The jump and its declining tail are one deal's cash flow, not a chart error.`
    : `This model holds flat through ${span}, then steps once in Year ${EY} (${fmt(real)}). The single jump is a one-time event, not a chart error.`;
}
